import {
  Box,
  HStack,
  Modal,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  useToken,
} from '@chakra-ui/react';
import {
  CarouselContent,
  CarouselProvider,
  HoverCloseButton,
} from '@time-line/components/common';
import { Media } from 'components/reviews/ProductReviewMedia/Media';
import { useState } from 'react';
import type { JunipPhoto } from 'types/reviews/JunipPhoto';
import type { JunipVideo } from 'types/reviews/JunipVideo';

import { ProductReviewMediaCarouselButton } from './ProductReviewMediaCarouselButton';
import { ProductReviewMediaThumbnail } from './ProductReviewMediaThumbnail';

const initialSlide = 0;
const thumbnailSize = 80;

interface ProductReviewMediaProps {
  id: string;
  mediaUrls: (JunipPhoto | JunipVideo)[];
}

const isVideo = (item: JunipPhoto | JunipVideo) =>
  !!(item as JunipVideo).original;

export function ProductReviewMedia({ id, mediaUrls }: ProductReviewMediaProps) {
  const gray50 = useToken('colors', 'gray.50');
  const whiteAlpha500 = useToken('colors', 'whiteAlpha.500');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeSlide, setActiveSlide] = useState<number>(initialSlide);

  return (
    <Box>
      <HStack spacing="5xs">
        {mediaUrls.map((item, mediaUrlIndex) => (
          <ProductReviewMediaThumbnail
            key={mediaUrlIndex}
            onClick={() => {
              if (!isOpen) {
                setActiveSlide(mediaUrlIndex);
                onOpen();
              }
            }}
            index={mediaUrlIndex}
            isVideo={isVideo(item)}
            size={thumbnailSize}
            image={item.thumbnail || undefined}
            playButtonVariant="light"
          />
        ))}
      </HStack>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay background="blackAlpha.400" />
        <ModalContent marginTop={0} maxHeight="100dvh" maxWidth="56vh">
          <Box
            position="relative"
            width="100%"
            height="100dvh"
            my={{ base: 0, md: 'lg' }}
            bg="black"
            borderRadius={{ base: 'none', md: 'base' }}
            role="group"
            overflow="hidden"
            sx={{
              '.swiper-horizontal': {
                width: '100%',
                height: '100%',
                '--swiper-pagination-bullet-horizontal-gap': '5px',
                '--swiper-pagination-color': gray50,
                '--swiper-pagination-bullet-inactive-color': whiteAlpha500,
                '--swiper-pagination-bullet-inactive-opacity': '1',
                '--swiper-pagination-bullet-size': '6px',
                '.swiper-pagination-horizontal': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bottom: '2xs',
                },
                '.swiper-slide': {
                  opacity: 0,
                  transitionProperty: 'common',
                  transitionDuration: 'normal',
                  transitionDelay: '0.5s',
                },
                '.swiper-slide-visible': {
                  opacity: 1,
                  transitionDelay: '0s',
                },
              },
            }}
          >
            <CarouselProvider>
              <CarouselContent
                key={id}
                slidesPerView={1}
                watchSlidesProgress
                showPagination
                initialSlide={activeSlide}
                a11y={{}}
                onSlideChange={(swiper) => {
                  setActiveSlide(swiper.realIndex);
                }}
              >
                {mediaUrls.map((item, mediaUrlIndex) => (
                  <Media
                    key={item.thumbnail}
                    index={mediaUrlIndex}
                    media={item}
                    isVideo={isVideo(item)}
                    isActive={activeSlide === mediaUrlIndex}
                  />
                ))}
              </CarouselContent>
              <HoverCloseButton
                onClick={onClose}
                position="absolute"
                top="xs"
                right="xs"
                zIndex={1}
                _groupHover={{ opacity: 1, background: 'gray.50' }}
                aria-label="Close modal"
              />
              <Box display={activeSlide === 0 ? 'none' : 'block'}>
                <ProductReviewMediaCarouselButton
                  direction="prev"
                  variant="white"
                />
              </Box>
              <Box
                display={
                  activeSlide + 1 === mediaUrls.length ? 'none' : 'block'
                }
              >
                <ProductReviewMediaCarouselButton
                  direction="next"
                  variant="white"
                />
              </Box>
            </CarouselProvider>
          </Box>
        </ModalContent>
      </Modal>
    </Box>
  );
}
